import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

interface ActivityTrayState {
    /** One-shot trigger for programmatic opening (e.g. from toast hooks). */
    isOpen: boolean;
    /** Actual tray visibility — synced by trigger components (Popover/Dialog). */
    trayVisible: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    setTrayVisible: (visible: boolean) => void;
}

/**
 * Lets external code (e.g. toast notifications) programmatically open the
 * activity tray, which otherwise lives inside a Popover with its own state.
 *
 * `trayVisible` tracks actual visibility — trigger components sync it via
 * `setTrayVisible`. Consumers subscribe to transitions via Zustand's
 * `subscribe` with selector (e.g. to dismiss toasts on tray open).
 */
export const useActivityTrayStore = create<ActivityTrayState>()(
    subscribeWithSelector((set) => ({
        isOpen: false,
        trayVisible: false,
        open: () => set({ isOpen: true }),
        close: () => set({ isOpen: false }),
        toggle: () => set((s) => ({ isOpen: !s.isOpen })),
        setTrayVisible: (visible) => set({ trayVisible: visible })
    }))
);
